// src/hooks/useWildfireSearch.ts
// Hook for searching wildfires via Drupal JSON:API (Prototype 3)

import { useState, useCallback, useRef, useEffect } from 'react';
import { getCmsApiUrl } from '../services/configService';

export interface WildfireResult {
  id: string;
  internalId: number;
  name: string;
}

export interface UseWildfireSearchOptions {
  /** When true, restrict matches to terms ending with the current 2-digit year (e.g. "-26"). */
  currentYearOnly?: boolean;
}

export function useWildfireSearch(options: UseWildfireSearchOptions = {}) {
  const [results, setResults] = useState<WildfireResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const API_URL = getCmsApiUrl();
  const { currentYearOnly = false } = options;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const search = useCallback((query: string) => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      return;
    }

    // Debounce the search
    debounceRef.current = setTimeout(async () => {
      // In dev mode, CMS_URL is empty (uses proxy), so only check in prod

      setLoading(true);

      try {
        const params = new URLSearchParams({
          'filter[name][operator]': 'CONTAINS',
          'filter[name][value]': query,
          'page[limit]': '10'
        });

        if (currentYearOnly) {
          // JSON:API AND-combines filters with distinct names. Force path=name
          // since this filter group identifier ("year") differs from the field.
          const yy = String(new Date().getFullYear()).slice(-2);
          params.append('filter[year][operator]', 'ENDS_WITH');
          params.append('filter[year][value]', `-${yy}`);
          params.append('filter[year][path]', 'name');
        }

        const response = await fetch(
          `${API_URL}/jsonapi/taxonomy_term/wildfires?${params}`,
          {
            headers: { 'Accept': 'application/vnd.api+json' },
            credentials: 'include'
          }
        );

        if (response.ok) {
          const data = await response.json();
          setResults(data.data.map((term: Record<string, unknown>) => ({
            id: term.id,
            internalId: (term.attributes as Record<string, unknown>).drupal_internal__tid,
            name: (term.attributes as Record<string, unknown>).name
          })));
        }
      } catch (err) {
        console.error('Wildfire search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [API_URL, currentYearOnly]);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  return { results, loading, search, clearResults };
}
